<script setup>
import { ref, computed, watch, onBeforeMount, onMounted, toValue } from 'vue'
import { useI18n } from 'vue-i18n'
import MainContainer from '../components/js/Components/UI/Content/MainContainer.vue'
import PageLayout from '../components/js/Components/UI/PageLayout.vue'
import SimpleGroupSelect from '../components/js/Components/UI/Form/SimpleGroupSelect.vue'
import Calculator from '../components/js/Components/Home/Calculator.vue'
import ContractStatsBlock from '../components/js/Components/Dashboard/ContractStatsBlock.vue'
import QuotationStatsBlock from '../components/js/Components/Home/QuotationStatsBlock.vue'
import CompanySalesmanBlock from '../components/js/Components/Home/CompanySalesmanBlock.vue'
import DashboardContentSections from '../components/js/Components/Home/DashboardContentSections.vue'
import EligibilityTester from '../components/js/Components/Home/EligibilityTester.vue'
import EligibilityResultModal from '../components/js/Components/Home/EligibilityResultModal.vue'
import useApi from '../components/js/Composables/useApi'
import useUser from '../Composables/User/useUser.ts'
import useContractStats from '../components/js/Composables/useContractStats'
import useConfig from '../Composables/useConfig.ts'

const { t, te } = useI18n()
const { getJSON } = useApi()
const { user, hasRole } = useUser()
const { conf, hasFeature, isTheme } = useConfig()

// Quotation stats state
const statQuotation = ref({
  study: 0,
  accepted: 0,
  waiting: 0,
  refused: 0,
  canceled: 0,
})

const fullAmountQuotation = ref({
  study: 0,
  accepted: 0,
  waiting: 0,
  refused: 0,
  canceled: 0,
})

const quotationLoading = ref(false)
const quotationError = ref(null)

const fetchQuotationStats = async (searchParams = {}) => {
  quotationLoading.value = true
  quotationError.value = null
  try {
    const query = new URLSearchParams()
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value) query.append(key, value)
    })

    const data = await getJSON(`quotations/stat${query.toString() ? '?' + query.toString() : ''}`)

    const newStatQuotation = {
      study: 0,
      accepted: 0,
      waiting: 0,
      refused: 0,
      canceled: 0,
    }
    const newFullAmountQuotation = {
      study: 0,
      accepted: 0,
      waiting: 0,
      refused: 0,
      canceled: 0,
    }

    for (let status of data) {
      if (status.fullAmount == null) {
        status.fullAmount = 0
      }

      switch (status.status) {
        case 'STUDY':
        case 'CLIENT_REQUEST':
        case 'REQUESTED':
        case 'DETAILLED_STUDY':
        case 'PREACCEPTED':
        case 'TO_BE_COMPLETED':
        case 'VALIDATION_REQUEST':
          newStatQuotation.study += status.nb
          newFullAmountQuotation.study += parseInt(status.fullAmount)
          break
        case 'CONDITIONAL':
        case 'ACCEPTED':
        case 'ACCEPTED_FORCED':
        case 'ACCEPTEDS':
        case 'EXPIRED':
          newStatQuotation.accepted += status.nb
          newFullAmountQuotation.accepted += parseInt(status.fullAmount)
          break
        case 'CLIENT_PROPOSAL':
        case 'CREATED':
          newStatQuotation.waiting += status.nb
          newFullAmountQuotation.waiting += parseInt(status.fullAmount)
          break
        case 'REFUSED':
        case 'NOT_ACCEPTED':
        case 'DEFINITIVE_REFUSAL':
          newStatQuotation.refused += status.nb
          newFullAmountQuotation.refused += parseInt(status.fullAmount)
          break
        case 'ERROR':
        case 'NORATE':
        case 'CANCELED':
          newStatQuotation.canceled += status.nb
          newFullAmountQuotation.canceled += parseInt(status.fullAmount)
          break
      }
    }

    statQuotation.value = newStatQuotation
    fullAmountQuotation.value = newFullAmountQuotation
  } catch (err) {
    quotationError.value = err
  } finally {
    quotationLoading.value = false
  }
}

const { contractStats, fetchContractStats } = useContractStats([
  'FINALIZE',
  'SIGNING',
  'DELIVERING',
  'PAYING',
  'PAID',
  'FINISH',
  'BUYOUT',
])

const sirenCode = ref('')
const company = ref('')
const eligible = ref(false)
const unknown = ref(false)
const hasError = ref(false)
const errorMessage = ref('')
const marketTypeId = ref('')
const marketTypeOptions = ref([])
const platformId = ref(null)
const dashboardLoading = ref(false)
const dashboard = ref(null)

const showEligibility = computed(() => hasFeature('dashboardQuotation') && user.value?.entity?.displayEligibility)
const showMarketTypeFilter = computed(() => hasFeature('dashboardMarketTypeFilter'))

const staticConfigContentSections = computed(() => {
  const sections = []
  const createSection = ({ title, color, items, visible = true }) => ({
    title,
    style: { color },
    items: items.map(({ label, file }) => ({
      title: te(label) ? t(label) : label,
      url: file !== '#' ? file : null,
    })),
    visible,
  })

  if (Array.isArray(conf.value?.dashboardNews)) {
    sections.push(
      createSection({
        title: t('home.dashboardNews.title'),
        color: 'danger',
        items: conf.value.dashboardNews,
      }),
    )
  }

  if (Array.isArray(conf.value?.dashboardSupport)) {
    sections.push(
      createSection({
        title: t('home.dashboardSupport.title'),
        color: 'success',
        items: conf.value.dashboardSupport,
      }),
    )
  }

  if (Array.isArray(conf.value?.dashboardHelp)) {
    sections.push(
      createSection({
        title: t('home.dashboardHelp.title'),
        color: 'info',
        items: conf.value.dashboardHelp,
        visible: !isTheme('olinn') || user.value.isBusinessMan,
      }),
    )
  }

  return sections
})

const contentSections = computed(() => {
  if (dashboardLoading.value) {
    return []
  }

  const availableContentSections = dashboard.value?.sections ?? staticConfigContentSections.value

  return availableContentSections.filter(({ visible = true }) => visible)
})

const availablePlatformsWithDashboard = computed(() => {
  return user.value.platforms?.filter((platform) => platform.hasDashboards) ?? []
})

const displayPlatformSelect = computed(() => availablePlatformsWithDashboard.value?.length > 1)

async function fetchDashboards() {
  const query = new URLSearchParams()

  query.set('platformId', platformId.value ?? availablePlatformsWithDashboard.value?.[0]?.id ?? null)

  try {
    dashboardLoading.value = true
    dashboard.value = await getJSON(`dashboards?${query.toString()}`)
  } catch {
    dashboard.value = null
  } finally {
    dashboardLoading.value = false
  }
}

async function fetchMarketTypes() {
  const marketTypes = await getJSON('markettypes')
  marketTypeOptions.value = [{ id: 0, name: t('home.dashboardMarketTypeFilter.all') }, ...marketTypes]
}

function setMarketTypeId(value) {
  marketTypeId.value = value
}

function handleEligibilityChecked(data) {
  errorMessage.value = ''
  hasError.value = false
  company.value = data.name
  eligible.value = data.isEligible
  unknown.value = !data.isEligible
  setTimeout(() => {
    $('#modalVerifySiren').modal()
  }, 0)
}

function handleEligibilityError(error) {
  errorMessage.value = error
  hasError.value = true
  setTimeout(() => {
    $('#modalVerifySiren').modal()
  }, 0)
}

function handleSirenChanged(val) {
  sirenCode.value = val
}

watch(marketTypeId, () => {
  fetchQuotationStats({ marketTypeId: marketTypeId.value })
  fetchContractStats({ marketTypeId: marketTypeId.value })
})

watch(platformId, async () => {
  await fetchDashboards()
})

onBeforeMount(() => {
  fetchMarketTypes()
  fetchQuotationStats()
  fetchContractStats()
})

onMounted(() => {
  platformId.value = user.value?.platforms?.length > 1 ? null : user.value?.platforms?.[0]?.id
  fetchDashboards()
})
</script>
<template>
  <MainContainer>
    <PageLayout menu="dashboard">
      <MainContainer>
        <div class="main-content">
          <div v-if="showMarketTypeFilter" class="row">
            <div class="col-md-6">
              <div class="bloc-col-md">
                <SimpleGroupSelect
                  v-if="showMarketTypeFilter"
                  id="marketType"
                  placeholder="home.dashboardMarketTypeFilter.all"
                  :config-options="marketTypeOptions"
                  :clear-button="false"
                  :search-input="false"
                  @transmitted="setMarketTypeId"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <QuotationStatsBlock
                v-if="hasFeature('dashboardSimul')"
                :quotation-stats="statQuotation"
                :quotation-amounts="fullAmountQuotation"
                :market-type-id="marketTypeId"
              />

              <ContractStatsBlock
                :contract-stats="contractStats"
                :show-dashboard-wallet="hasFeature('dashboardWallet')"
                :show-buyout="hasFeature('buyout')"
              />
            </div>

            <div v-if="!user.isReadOnly" class="col-md-6">
              <EligibilityTester
                v-if="showEligibility"
                @eligibility-checked="handleEligibilityChecked"
                @eligibility-error="handleEligibilityError"
                @siren-changed="handleSirenChanged"
              />

              <Calculator v-if="hasRole('ROLE_CALCULATOR')" data-test-id="calculator-widget" />

              <CompanySalesmanBlock
                v-if="hasFeature('dashboardContact') && user?.entity.companySalesmen.length > 0 && !user.isBusinessMan"
                :salesmen="user.entity.companySalesmen"
              />

              <div v-if="displayPlatformSelect" class="col-12">
                <SimpleGroupSelect
                  v-model.number="platformId"
                  :config-options="availablePlatformsWithDashboard"
                  placeholder="home.availablePlatformsWithDashboard.placeholder"
                />
              </div>

              <DashboardContentSections :sections="contentSections" :loading="dashboardLoading" />
            </div>
          </div>
        </div>
      </MainContainer>
    </PageLayout>

    <EligibilityResultModal
      :waiting="false"
      :eligible="eligible"
      :unknown="unknown"
      :has-error="hasError"
      :error-message="errorMessage"
      :company="company"
      :siren-code="sirenCode"
      :user-platforms="user.platforms"
    />
  </MainContainer>
</template>
